import type { SSEvent } from './event-store';

/**
 * Human-readable one-line summary of an SSE event. Single source of truth —
 * both the card (display) and the coalescer (grouping key) call this, so a
 * run of events only merges when it reads identically to a human.
 */
export function summarize(event: SSEvent): string {
  const d = event.data;
  switch (event.type) {
    case 'heartbeat': return `Beat #${d.count ?? d.heartbeatCount ?? '?'}`;
    case 'tool-call': case 'tool-start': return d.name ?? d.tool ?? 'tool';
    case 'system': return d.message ?? d.text ?? 'system event';
    case 'cron-result': return d.jobName ?? d.name ?? 'cron completed';
    case 'approval-request': return d.description ?? 'approval needed';
    case 'chat-response': return `${d.toolCalls?.length ?? 0} tool calls`;
    case 'delegation-status': return `${d.agentId ?? 'agent'}: ${d.status ?? ''}`;
    default: return event.type;
  }
}

/** A run of consecutive identical events collapsed into one display row. */
export interface EventGroup {
  /** The most recent event of the run (newest). */
  event: SSEvent;
  /** Number of events merged into this group (>= 1). */
  count: number;
  /** Oldest timestamp in the run. */
  firstTimestamp: number;
  /** Newest timestamp in the run. */
  lastTimestamp: number;
}

/**
 * Collapse consecutive runs of identical events into groups, for the
 * inspector activity feed. Input is newest-first (as stored). Two events
 * merge only when BOTH `type` and `summarize()` match AND they are adjacent
 * — an event of any other kind in between breaks the run. Group order follows
 * input order (newest group first).
 */
export function coalesceEvents(events: SSEvent[]): EventGroup[] {
  const groups: EventGroup[] = [];
  for (const event of events) {
    const key = `${event.type} ${summarize(event)}`;
    const last = groups[groups.length - 1];
    if (last && `${last.event.type} ${summarize(last.event)}` === key) {
      // Extend the current run. Input is newest-first, so `event` is older
      // than what's already in the group; widen firstTimestamp only.
      last.count += 1;
      last.firstTimestamp = Math.min(last.firstTimestamp, event.timestamp);
      last.lastTimestamp = Math.max(last.lastTimestamp, event.timestamp);
    } else {
      groups.push({
        event,
        count: 1,
        firstTimestamp: event.timestamp,
        lastTimestamp: event.timestamp,
      });
    }
  }
  return groups;
}
